import React from 'react';
import { DropdownProps, Menu } from 'semantic-ui-react';
import {
  debounce,
  getMediaPathResults,
  getYouTubeResults,
  isHttp,
  isMagnet,
  isYouTube,
} from '../../utils';
// import { examples } from '../../utils/examples';
import ChatVideoCard from '../Playlist/ChatVideoCard';

interface ComboBoxProps {
  setMedia: (e: any, data: DropdownProps) => void;
  playlistAdd: (e: any, data: DropdownProps) => void;
  playlistMove: (index: number, toIndex: number) => void;
  playlistDelete: (index: number) => void;
  currentMedia: string;
  getMediaDisplayName: (input: string) => string;
  launchMultiSelect: (multi: []) => void;
  mediaPath: string | undefined;
  streamPath: string | undefined;
  disabled?: boolean;
  playlist: PlaylistVideo[];
  togglePlaylist: () => void;
}

export class ComboBox extends React.Component<ComboBoxProps> {
  state = {
    inputMedia: '',
    results: undefined as JSX.Element[] | undefined,
    loading: false,
    lastResultTimestamp: Number(new Date()),
  };
  debounced: any = null;

  setMediaAndClose = (e: any, data: DropdownProps) => {
    window.setTimeout(
      () => this.setState({ inputMedia: '', results: undefined }),
      200
    );
    this.props.setMedia(e, data);
  };

  setUrlAndClose = (e: any) => {
    let firstResultUrl = this.state.results?.[0]?.key;
    if (typeof firstResultUrl === 'string' && firstResultUrl.startsWith('.$')) {
      firstResultUrl = firstResultUrl.substring(2);
    }
    if (firstResultUrl) {
      setTimeout(() => this.setState({ inputMedia: firstResultUrl }), 300);
      this.setMediaAndClose(e, { value: firstResultUrl });
      // this.setMediaAndClose(e, { value: firstResultUrl, inputMedia: firstResultUrl });
    } else {
      this.setState({ inputMedia: '', results: undefined });
    }
  };

  extractYouTubeId(url: string): string | null {
    try {
      const u = new URL(url.trim());
      const host = u.hostname.replace('www.', '');

      // youtu.be/VIDEO_ID
      if (host === 'youtu.be') {
        return u.pathname.split('/')[1] || null;
      }

      if (host.endsWith('youtube.com')) {
        // watch?v=VIDEO_ID
        const v = u.searchParams.get('v');
        if (v) return v;

        const pathParts = u.pathname.split('/').filter(Boolean);

        // embed/VIDEO_ID
        const embedIndex = pathParts.indexOf('embed');
        if (embedIndex > -1 && pathParts[embedIndex + 1]) {
          return pathParts[embedIndex + 1];
        }

        const shortsIndex = pathParts.indexOf('shorts');
        if (shortsIndex > -1 && pathParts[shortsIndex + 1]) {
          return pathParts[shortsIndex + 1];
        }
      }
    } catch {
      // Not a URL or malformed input
    }
    return null;
  }

  doSearch = async (e: any) => {
    e.persist();
    this.setState({ inputMedia: e.target.value }, () => {
      if (!this.debounced) {
        this.debounced = debounce(async () => {
          this.setState({ loading: true });
          const query: string = this.state.inputMedia;
          let timestamp = Number(new Date());
          let results: JSX.Element[] | undefined = undefined;
          if (
            query === '' ||
            // Anything that doesn't pass this check we pass to YouTube as a search query
            (query && (isHttp(query) || isMagnet(query)))
          ) {
            // let items = examples;
            let items;
            if (!this.state.inputMedia && this.props.mediaPath) {
              items = await getMediaPathResults(this.props.mediaPath, '');
            }
            if (query) {
              let type = 'file';
              if (isYouTube(query)) {
                type = 'youtube';
              }
              if (isMagnet(query)) {
                type = 'magnet';
              }
              items = [
                {
                  name: query,
                  type,
                  url: query,
                  duration: 0,
                  img: isYouTube(this.state.inputMedia!)
                    ? (() => {
                        const id = this.extractYouTubeId(
                          this.state.inputMedia!
                        );
                        return id
                          ? `https://img.youtube.com/vi/${id}/hqdefault.jpg`
                          : '';
                      })()
                    : '',
                },
              ];
            }
            results = items?.map((result: SearchResult, index: number) => (
              <button
                className="w-full"
                key={result.url}
                onClick={(e: any) => {
                  setTimeout(
                    () => this.setState({ inputMedia: result.url }),
                    300
                  );
                  this.setMediaAndClose(e, { value: result.url });
                }}
              >
                <ChatVideoCard
                  video={result}
                  index={index}
                  onPlaylistAdd={this.props.playlistAdd}
                />
              </button>
            ));
          } else {
            const data = await getYouTubeResults(query);
            results = data?.map((result, index) => {
              return (
                <Menu.Item
                  key={result.url}
                  onClick={(e: any) => {
                    setTimeout(
                      () => this.setState({ inputMedia: result.url }),
                      300
                    );
                    this.setMediaAndClose(e, { value: result.url });
                  }}
                >
                  <ChatVideoCard
                    video={result}
                    index={index}
                    onPlaylistAdd={this.props.playlistAdd}
                  />
                </Menu.Item>
              );
            });
          }
          if (timestamp > this.state.lastResultTimestamp) {
            this.setState({
              loading: false,
              results,
              lastResultTimestamp: timestamp,
            });
          }
        }, 500);
      }
      this.debounced();
    });
  };

  componentDidMount(): void {
    const { currentMedia, getMediaDisplayName } = this.props;

    if (currentMedia) {
      this.setState({
        inputMedia: getMediaDisplayName(currentMedia),
      });
    }
  }

  componentDidUpdate(prevProps: Readonly<ComboBoxProps>): void {
    if (prevProps.currentMedia && !this.props.currentMedia) {
      this.setState({ inputMedia: '' });
    }
  }

  render() {
    const { currentMedia, getMediaDisplayName } = this.props;
    const { results } = this.state;
    return (
      <div className="relative">
        <div className="flex gap-1 w-full items-end">
          <form autoComplete="off" className="w-full">
            <h4 className="text-[16px] text-gradient-blue inline-block mt-3 mb-4">
              Now Watching
            </h4>
            <div className="relative overflow-hidden p-[1px] after:content-[''] after:absolute after:inset-0 after:rounded-[8px] after:p-[5px] after:pointer-events-none after:z-0 gradient-pink-border">
              <div className="relative z-[1] flex items-center overflow-hidden p-4 pr-24 rounded-[8px] bg-[#040404]">
                <input
                  type="text"
                  className="text-white bg-transparent border-none w-full selection:bg-[#040404] selection:text-white"
                  placeholder="Enter video file URL"
                  disabled={this.props.disabled}
                  onChange={this.doSearch}
                  onFocus={(e: any) => {
                    if (!this.state.inputMedia) return;
                    e.persist();
                    this.setState(
                      {
                        inputMedia:
                          isHttp(currentMedia) || isMagnet(currentMedia)
                            ? currentMedia
                            : getMediaDisplayName(currentMedia),
                      },
                      () => {
                        if (
                          this.state.inputMedia &&
                          (isHttp(this.state.inputMedia) ||
                            isMagnet(this.state.inputMedia))
                        ) {
                          this.doSearch(e);
                        }
                      }
                    );
                    setTimeout(() => e.target.select(), 100);
                  }}
                  onBlur={() => {
                    setTimeout(
                      () => this.setState({ results: undefined }),
                      200
                    );
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      this.setMediaAndClose(e, {
                        value: this.state.inputMedia,
                      });
                    }
                  }}
                  // loading={this.state.loading}
                  value={this.state.inputMedia}
                />
                {this.state.inputMedia && (
                  <div className="absolute right-4">
                    <button
                      className="p-0"
                      onClick={(e) => this.setUrlAndClose(e)}
                    >
                      <i className="ri-close-line text-gradient-pink text-2xl hover:cursor-pointer"></i>
                    </button>
                    <button
                      className="p-0"
                      onClick={(e: any) => {
                        this.setState({ inputMedia: '', results: undefined });
                        this.setMediaAndClose(e, {
                          value: '',
                        });
                      }}
                    >
                      <i className="ri-contract-right-fill text-gradient-pink text-2xl hover:cursor-pointer"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </form>
          {this.props.playlist.length > 0 && (
            <button
              className="gradient-btn text-black flex-shrink-0 flex items-center gap-1 rounded-lg justify-center text-2xl duration-200  hover:opacity-70 size-[51px]"
              onClick={this.props.togglePlaylist}
            >
              <i className="ri-play-list-2-fill"></i>
            </button>
          )}
          {/* <Dropdown
            icon="list"
            labeled
            className="icon"
            button
            text={`Playlist (${this.props.playlist.length})`}
            scrolling
          >
            <Dropdown.Menu direction="left">
              {this.props.playlist.length === 0 && (
                <Dropdown.Item disabled>
                  There are no items in the playlist.
                </Dropdown.Item>
              )}
              {this.props.playlist.map((item: PlaylistVideo, index: number) => {
                if (Boolean(item.img)) {
                  item.type = 'youtube';
                }
                return (
                  <Dropdown.Item>
                    <div style={{ maxWidth: '500px' }}>
                      <ChatVideoCard
                        video={item}
                        index={index}
                        controls
                        onPlay={(index) => {
                          this.props.setMedia(null, {
                            value: this.props.playlist[index]?.url,
                          });
                          this.props.playlistDelete(index);
                        }}
                        onPlayNext={(index) => {
                          this.props.playlistMove(index, 0);
                        }}
                        onRemove={(index) => {
                          this.props.playlistDelete(index);
                        }}
                        disabled={this.props.disabled}
                      />
                    </div>
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown> */}
        </div>
        {Boolean(results) && this.state.inputMedia !== undefined && (
          <div className="absolute z-[9999] w-full">
            <div className="relative overflow-hidden p-[1px] after:content-[''] after:absolute after:inset-0 after:rounded-[8px] after:p-[5px] after:pointer-events-none after:z-0 gradient-pink-border">
              <div className="relative z-[1] overflow-hidden rounded-[8px] bg-[#040404] text-white">
                {results}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}
